// src/components/Nodes/ChooseProductsNode/ChooseProductModal.js

import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const ChooseProductModal = ({
  isOpen,
  onClose,
  nodeData,
  onSave,
  addressNodeFields,
}) => {
  const [inputFields, setInputFields] = useState(addressNodeFields || []);
  const [mappedField, setMappedField] = useState(null);

  useEffect(() => {
    if (nodeData && nodeData.parameters) {
      // Load the saved mapped field if it exists
      const savedMappedField = nodeData.parameters.serviceArea || {};
      if (Object.keys(savedMappedField).length > 0) {
        const fieldName = Object.keys(savedMappedField)[0];
        setMappedField({ name: fieldName, value: savedMappedField[fieldName] });
      }
    }
  }, [nodeData]);

  useEffect(() => {
    setInputFields(inputFields);
  }, [inputFields]);

  const handleDragStart = (event, field) => {
    event.dataTransfer.setData('field', JSON.stringify(field));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const field = JSON.parse(event.dataTransfer.getData('field'));
    setMappedField(field);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleRemoveMappedField = () => {
    setMappedField(null);
  };

  const handleSave = () => {
    const serviceArea = mappedField
      ? { [mappedField.name]: mappedField.value || '' }
      : {};
    const updatedData = {
      ...nodeData,
      parameters: { ...nodeData.parameters, serviceArea },
    };
    onSave(updatedData);
    onClose();
  };

  return isOpen ? (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2>Choose Product Configuration</h2>
          <FaTimes onClick={onClose} style={modalStyles.closeIcon} />
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.columnContainer}>
            <div style={modalStyles.column}>
              <h3>Input</h3>
              <div style={modalStyles.hierarchyContainer}>
                <div style={modalStyles.parentNode}>
                  <h4>Address Node</h4>
                  <div style={modalStyles.fieldContainer}>
                    {inputFields.map((field) => (
                      <div
                        key={field.name}
                        style={modalStyles.fieldRow}
                        draggable
                        onDragStart={(event) => handleDragStart(event, field)}
                      >
                        <span>{field.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div style={modalStyles.divider}></div>
            <div
              style={modalStyles.column}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <h3>Service Area</h3>
              <div style={modalStyles.boxContainer}>
                {mappedField ? (
                  <div style={modalStyles.mappedFieldRow}>
                    <span>{mappedField.name}</span>
                    <FaTimes
                      onClick={handleRemoveMappedField}
                      style={modalStyles.removeIcon}
                    />
                  </div>
                ) : (
                  <div style={modalStyles.placeholder}>Drag a field here</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={modalStyles.actions}>
          <button onClick={handleSave} style={modalStyles.saveButton}>
            Save
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '700px',
    padding: '20px',
    boxSizing: 'border-box',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    maxHeight: '80vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    position: 'relative',
  },
  closeIcon: {
    cursor: 'pointer',
    fontSize: '18px',
    color: '#333',
    position: 'absolute',
    top: '-10px',
    right: '-10px',
  },
  body: {
    display: 'flex',
    justifyContent: 'space-between',
    flexGrow: 1,
  },
  columnContainer: {
    display: 'flex',
    flexGrow: 1,
  },
  column: {
    flex: 1,
    padding: '10px',
    boxSizing: 'border-box',
    maxHeight: '60vh',
    overflowY: 'auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
  divider: {
    width: '2px',
    backgroundColor: '#ddd',
    margin: '0 20px',
  },
  hierarchyContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  parentNode: {
    backgroundColor: '#f1f1f1',
    padding: '10px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  boxContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80px',
    border: '2px dashed #ddd',
    borderRadius: '8px',
    backgroundColor: '#f1f1f1',
    marginTop: '10px',
  },
  mappedFieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    margin: '0 20px 0 20px',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    width: '100%',
    position: 'relative',
  },
  placeholder: {
    color: '#888',
  },
  removeIcon: {
    cursor: 'pointer',
    color: '#d9534f',
    fontSize: '18px',
    position: 'absolute',
    right: '-1%',
    top: '10%',
    transform: 'translateY(-50%)',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
  },
  saveButton: {
    padding: '10px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'var(--primary-color)',
    color: '#fff',
    textAlign: 'center',
  },
};

export default ChooseProductModal;
